import React, { FunctionComponent, useState, useEffect } from "react";
import { Box, Button, SxProps, IconButton, Tooltip } from "@mui/material";
import { Dialog, COLORS, TextField, Loading } from "components";
import { ColorType } from "types";
import { EsgPencilIcon, EyeIcon } from "assets";
import { processHtmlContent } from "utils";

const longTextFieldSx = {
    width: '500px',
    height: '200px',
    "& .MuiInputBase-root.MuiOutlinedInput-root": {
        height: '200px !important',
        alignItems: 'flex-start',
        fontSize: '12px',
        borderRadius: '8px',
        padding: '0 !important',
        "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: '8px'
        },
        "& .MuiInputBase-input": {
            height: '200px !important',
            minHeight: '200px !important',
            maxHeight: '200px !important',
            overflow: 'auto !important',
            wordWrap: 'break-word',
            whiteSpace: 'pre-wrap',
            fontSize: '14px',
            padding: '8px 12px !important',
            margin: '0 !important',
            boxSizing: 'border-box !important',
            '&::placeholder': {
                fontSize: '14px'
            }
        }
    }
}
interface InputNameDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (templateName: string) => void;
    mainTitle?: string;
    dialogTitle?: string;
    placeholder?: string;
    label?: string;
    isLoading?: boolean;
    submitButtonText?: string;
    cancelButtonText?: string;
    defaultValue?: string;
    loadingDefaultValue?: boolean;
    textFieldSx?: SxProps;
    longTextField?: boolean;
    readOnly?: boolean;
    enableEdit?: boolean;
    allowEmptyValue?: boolean;
}

export const InputNameDialog: FunctionComponent<InputNameDialogProps> = ({
    isOpen,
    onClose,
    onSubmit,
    mainTitle,
    dialogTitle,
    placeholder,
    label,
    isLoading = false,
    submitButtonText = "Save",
    cancelButtonText = "Cancel",
    defaultValue = "",
    loadingDefaultValue,
    textFieldSx,
    longTextField = false,
    readOnly = false,
    enableEdit = false,
    allowEmptyValue = false,
}) => {
    const [inputText, setInputText] = useState(defaultValue);
    const [isEditMode, setIsEditMode] = useState(!readOnly);
    const [originalValue, setOriginalValue] = useState(defaultValue);

    useEffect(() => {
        setInputText(defaultValue);
        setOriginalValue(defaultValue);
    }, [defaultValue]);

    useEffect(() => {
        setIsEditMode(!readOnly);
    }, [readOnly]);

    const handleSubmit = () => {
        if (allowEmptyValue || inputText.trim()) {
            onSubmit(inputText.trim());
            setOriginalValue(inputText.trim());
            setInputText("");
        }
    };

    const handleClose = () => {
        setInputText(defaultValue);
        setOriginalValue(defaultValue);
        setIsEditMode(!readOnly);
        onClose();
    };

    const handleEditClick = () => {
        setIsEditMode(!isEditMode);
    };

    // Check if content has changed from original value
    const hasContentChanged = (inputText || '').trim() !== (originalValue || '').trim();

    return (
        <Dialog isOpen={isOpen} dividers onClose={handleClose} title={dialogTitle ? <div style={{ color: COLORS.Mainblue, fontSize: '12px', fontWeight: '600' }}>{dialogTitle}</div> : <div />}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ color: COLORS.Mainblue, fontSize: '20px', fontWeight: '500' }}>
                        {mainTitle}
                    </div>
                    {enableEdit && (
                        <Tooltip arrow placement="top" title={isEditMode ? "Read only" : "Edit"}>
                            <IconButton
                                onClick={handleEditClick}
                                size="small"
                                sx={{
                                    color: COLORS.Mainblue,
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                    }
                                }}
                            >
                                {isEditMode ? <EyeIcon /> : <EsgPencilIcon />}
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                {loadingDefaultValue || isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', width: '492px' }}>
                        <Loading sx={{
                            minWidth: 50,
                            minHeight: 50,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }} />
                    </Box>
                ) : !isEditMode ? (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        ...(longTextField ? { width: '500px', height: '200px' } : {})
                    }}>
                        <Box
                            sx={{
                                flex: 1,
                                fontSize: '14px',
                                lineHeight: '1.4',
                                wordWrap: 'break-word',
                                whiteSpace: 'pre-wrap',
                                overflow: 'auto',
                                height: '200px',
                                width: '500px',
                                maxHeight: longTextField ? '200px' : 'auto',
                                padding: '8px 12px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                backgroundColor: '#f9f9f9',
                                '& a': {
                                    color: COLORS.Mainblue,
                                    textDecoration: 'underline',
                                    '&:hover': {
                                        textDecoration: 'none'
                                    }
                                }
                            }}
                            dangerouslySetInnerHTML={{ __html: processHtmlContent(inputText) }}
                        />

                    </Box>
                ) : (
                    <TextField
                        autoFocus
                        label={label}
                        type="text"
                        fullWidth
                        multiline
                        sxLabel={{ color: COLORS.Mainblue }}
                        color={'primary' as ColorType}
                        sx={{
                            "& .MuiInputBase-root.MuiOutlinedInput-root": {
                                fontSize: '12px',
                                border: '0px',
                                "& .MuiOutlinedInput-notchedOutline": { borderRadius: '8px', borderWidth: '1px' }
                            },
                            ...(longTextField ? longTextFieldSx : {}),
                            ...textFieldSx
                        }}
                        variant="outlined"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder={placeholder}
                    />
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "10px", mt: "20px" }}>
                    <Button onClick={handleClose}>
                        {cancelButtonText}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isLoading || (!allowEmptyValue && !(inputText || '').trim()) || !hasContentChanged}
                        variant="contained"
                    >
                        {isLoading ? 'Saving...' : submitButtonText}
                    </Button>
                </Box>

            </Box>
        </Dialog>
    );
};
